import React, { useState } from 'react'
import { Button, Popover, Typography } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'

import ValidatedTextInput from '../ValidatedTextInput'

/* styles that will be passed to the SignUp component */
const styles = theme => ({
  signUpButtonTopBar: {
    marginLeft: theme.spacing.unit
  },

  /* wrapper for the form fields */
  verticalWrapper: {
    display: 'flex',
    flexDirection: 'column'
  },

  /* style for the input wrapper */
  formControl: {
    margin: theme.spacing.unit
  },

  /* style for text boxes on the popup */
  textField: {
    width: 300
  },

  /* style for error labels */
  errorHelperText: {
    color: 'red'
  },

  /* style for SignUp button */
  buttonSignUp: {
    width: '90%',
    float: 'center',
    margin: theme.spacing.unit
  },

  notification: {
    margin: theme.spacing.unit
  }
})

/* Component that implements the "SignUp" button of the top bar
and the pop-up attached to it */
const SignUp = ({
  classes,
  inputValues,
  validationErrors,
  isSignUpRequestPending,
  isSignUpRequestSuccessful,
  isSignUpRequestError,
  onEmailInputChange,
  onFirstNameInputChange,
  onLastNameInputChange,
  onPasswordInputChange,
  onPasswordConfirmationInputChange,
  onFormSubmit
}) => {
  const { t } = useTranslation()

  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  // function called when TopBar "Sign Up" button is clicked, shows SignUp form popup
  const onTopBarButtonClick = (event) => setAnchorEl(event.currentTarget)

  // disable the submit button, if there are any validation errors or an empty field
  const submitButtonDisabled = Object.values(validationErrors).reduce(
    (acc, value) => acc || value, false
  ) || !Object.values(inputValues).reduce((acc, value) => acc && value, true)

  return (
    <div className={classes.signUpButtonTopBar}>
      <Button
        aria-owns={open ? 'simple-popper' : undefined}
        aria-haspopup='true'
        variant='contained'
        onClick={onTopBarButtonClick}
      >
        {t('TopBar:sign up')}
      </Button>

      <Popover
        id='simple-popper'
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <div className={classes.verticalWrapper}>

          {
            isSignUpRequestPending ? ( // if request is pending -- display loading view
              <Typography variant='overline' className={classes.notification}>
                { t('TopBar:loading') }
              </Typography>
            ) : isSignUpRequestSuccessful ? ( // if request has been successful -- display success message
              <Typography variant='overline' className={classes.notification}>
                { t('TopBar:verification email has been sent') }
              </Typography>
            ) : isSignUpRequestError ? ( // if request has failed -- display error message
              <Typography variant='overline' className={classes.notification}>
                { t('TopBar:server unavailable') }
              </Typography>
            ) : ( // if none of the above -- display the sign up form
              <>
                {/* Email text input */}
                <ValidatedTextInput
                  classes={classes}
                  value={inputValues.email}
                  onChange={onEmailInputChange}
                  placeholder={t('TopBar:email')}
                  type='email'
                  autoComplete='email'
                  validationError={t(validationErrors.email)}
                />

                {/* First name text input */}
                <ValidatedTextInput
                  classes={classes}
                  value={inputValues.firstName}
                  onChange={onFirstNameInputChange}
                  placeholder={t('TopBar:first name')}
                  autoComplete='given-name'
                  validationError={t(validationErrors.firstName)}
                />

                {/* Last name text input */}
                <ValidatedTextInput
                  classes={classes}
                  value={inputValues.lastName}
                  onChange={onLastNameInputChange}
                  placeholder={t('TopBar:last name')}
                  autoComplete='family-name'
                  validationError={t(validationErrors.lastName)}
                />

                {/* Password text input */}
                <ValidatedTextInput
                  classes={classes}
                  value={inputValues.password}
                  onChange={onPasswordInputChange}
                  placeholder={t('TopBar:password')}
                  type='password'
                  autoComplete='current-password'
                  validationError={t(validationErrors.password)}
                />

                {/* Password confirmation text input */}
                <ValidatedTextInput
                  classes={classes}
                  value={inputValues.confirmPassword}
                  onChange={onPasswordConfirmationInputChange}
                  placeholder={t('TopBar:confirm password')}
                  type='password'
                  autoComplete='current-password'
                  validationError={t(validationErrors.confirmPassword)}
                />

                <div>
                  <Button
                    disabled={submitButtonDisabled}
                    onClick={() => onFormSubmit(inputValues)}
                    variant='contained'
                    color='primary'
                    className={classes.buttonSignUp}>
                    {t('TopBar:sign up')}
                  </Button>
                </div>
              </>
            )}

        </div>
      </Popover>
    </div>
  )
}

export default withStyles(styles)(SignUp)
